// Fitted model states, fetched from this app's own server.
//
// Elo's ratings come from five seasons of results that the phone has never
// seen and does not want to download. The server publishes the finished state
// as a few kilobytes of JSON (scripts/publish-models.mjs); this fetches it
// once per session and binds it into the shape `runStrategy` consumes.
//
// A missing or unreachable state is not an error worth showing: the strategy
// that wanted the model simply produces no tickets and says so in its notes.

import { apiBase } from './api-base.js';
import { bindModel, modelFor } from './engine/models/index.js';

/** The signed-in punter's token as a header. Imported late so a build without auth still runs. */
async function authHeaders() {
  try {
    const mod = await import('../../addons/auth/index.js');
    const token = await mod.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

// modelId:sport -> state. One fetch per model per session; the server sets a
// one-hour cache header for everything after that.
const CACHE = new Map();

/**
 * The published state for a model in a sport, or null if there isn't one —
 * never rejects, and never throws.
 */
export async function loadModelState(modelId, sport) {
  const cacheKey = `${modelId}:${sport}`;
  if (CACHE.has(cacheKey)) return CACHE.get(cacheKey);

  let state = null;
  try {
    const res = await fetch(`${apiBase()}/api/models/${encodeURIComponent(modelId)}/${encodeURIComponent(sport)}`, {
      headers: { Accept: 'application/json', ...(await authHeaders()) },
    });
    if (res.ok && /json/.test(res.headers.get('content-type') ?? '')) {
      const body = await res.json();
      state = body?.state ?? null;
    }
  } catch {
    state = null;
  }
  CACHE.set(cacheKey, state);
  return state;
}

/** A loaded state as `ctx.models[modelId]`, or null when the model is unknown. */
export function bindLoaded(modelId, state) {
  const model = modelFor(modelId);
  return model ? bindModel(model, state) : null;
}

/** Forget everything fetched — for tests, and for a manual refresh. */
export function clearModelCache() {
  CACHE.clear();
}
